import * as React from 'react';
import styled from 'styled-components';

import { getSlateEditorValue } from '@doltech/utils/lib/editor';
import { RendererWithPlugins } from '../../../slatePlugins/components/core/readonly/RendererWithPlugins';
import { ParagraphPlugin } from '../../../slatePlugins/components/core/readonly/ParagraphPlugin';
import { BoldReadOnlyPlugin } from '../../../slatePlugins/marks/bold/BoldReadOnlyPlugin';
import { ItalicReadOnlyPlugin } from '../../../slatePlugins/marks/italic/ItalicReadOnlyPlugin';
import { UnderlineColorReadOnlyPlugin } from '../../DolEditor/marks/underline-color/UnderlineColorPlugin';
import { StrikethroughReadOnlyPlugin } from '../../../slatePlugins/marks/strikethrough/StrikethroughReadOnlyPlugin';
import { IconReadOnlyPlugin } from '../../DolEditor/elements/icons/IconPlugin';
import { HeadingReadOnlyPlugin } from '../../../slatePlugins/elements/heading/HeadingReadOnlyPlugin';
import { HighlightElementReadOnlyPlugin } from '../../DolEditor/elements/highlight/HighlightReadOnlyPlugin';
import { RoundedElementReadOnlyPlugin } from '../../DolEditor/elements/rounded/plugin';
import { TextColorReadOnlyPlugin } from '../../DolEditor/marks/text-color/TextColorPlugin';
import { BulletPointReadOnlyPlugin } from '../../BlogEditor/plugins/BulletPoint/BulletPointReadOnlyPlugin';
import { QuoteReadOnlyPlugin } from '../../BlogEditor/plugins/Quote/QuoteReadOnlyPlugin';
import { QuestionStyleReadOnlyPlugin } from '../../BlogEditor/plugins/QuestionStyle/QuestionStyleReadOnlyPlugin';
import { DEFAULT_HIGHLIGHT_COLORS_LIST } from '../../DolEditor/elements/highlight/defaults';
import { colorsV2 } from '../../colors-v2';
import { TextOnlyEditor, TextOnlyEditorProps } from '../../BlogEditor/TextOnlyEditor';
import { TextTransformPlugin } from '../../BlogEditor/plugins/TextTransform/TextTransformPlugin';
import { AbbrReadOnlyPlugin } from '../../../slatePlugins/marks/abbr/AbbrReadOnlyPlugin';
import { CiteReadOnlyPlugin } from '../../../slatePlugins/marks/cite/CiteReadOnlyPlugin';
import { QReadOnlyPlugin } from '../../../slatePlugins/marks/q/QReadOnlyPlugin';
import { HyperLinkElementPlugin } from '../../DolEditor/elements/hyper-link/plugin';
import cl from 'classnames';

const ROW_HEIGHT = 26;

const Container = styled.div<{ rows?: number }>`
  .dol-editor {
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;

    min-height: ${(p) => p.rows * ROW_HEIGHT}px;
    padding: 12px 16px;
    background-color: transparent;

    [data-slate-placeholder='true'] {
      opacity: 1 !important;
      color: ${colorsV2.black60};
    }
  }

  &.noPadding {
    .dol-editor {
      padding: 0;
    }
  }

  &.readonly {
    p {
      margin: 0;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: ${colorsV2.black100};
      white-space: pre-wrap;

      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }

    strong {
      font-weight: 600;
    }
  }
`;

const plugins = [
  ParagraphPlugin,
  BoldReadOnlyPlugin(),
  HeadingReadOnlyPlugin(),
  HighlightElementReadOnlyPlugin({
    colors: DEFAULT_HIGHLIGHT_COLORS_LIST,
  }),
  ItalicReadOnlyPlugin(),
  UnderlineColorReadOnlyPlugin(),
  StrikethroughReadOnlyPlugin(),
  BulletPointReadOnlyPlugin(),
  TextColorReadOnlyPlugin(),
  QuoteReadOnlyPlugin(),
  IconReadOnlyPlugin(),
  RoundedElementReadOnlyPlugin(),
  QuestionStyleReadOnlyPlugin(),
  TextTransformPlugin(),
  AbbrReadOnlyPlugin(),
  CiteReadOnlyPlugin(),
  QReadOnlyPlugin(),
  HyperLinkElementPlugin({ isReadOnly: true }),
];

interface TextAreaContentEditorProps
  extends Pick<
    TextOnlyEditorProps,
    'stickDirection' | 'stickSpacing' | 'overrideToolbar' | 'plugins'
  > {
  value?: string;
  placeholder?: string;
  rows?: number;
  onChange?: (changedValue: string) => void;
  readonly?: boolean;
  topOveride?: number;
  noPadding?: boolean;
  onMounted?: (editor: any) => void;
}

export const TextAreaContentEditor = (props: TextAreaContentEditorProps) => {
  const {
    value = '',
    placeholder,
    rows = 1,
    stickDirection = 'right',
    stickSpacing = 32,
    overrideToolbar,
    onChange,
    topOveride,
    readonly = false,
    noPadding,
    ...rest
  } = props;

  if (readonly) {
    return (
      <Container className="text-area-content-editor readonly" rows={rows}>
        <RendererWithPlugins plugins={rest.plugins || plugins} value={getSlateEditorValue(value)} />
      </Container>
    );
  }

  return (
    <Container className={cl('text-area-content-editor', { noPadding })} rows={rows}>
      <TextOnlyEditor
        placeholder={placeholder}
        editorClassName="small-paragraph-spacing"
        overrideToolbar={overrideToolbar}
        initialValue={getSlateEditorValue(value)}
        stickDirection={stickDirection}
        stickSpacing={stickSpacing}
        topOveride={topOveride}
        onEditorChange={(newValue) => onChange?.(JSON.stringify(newValue))}
        isReadOnly={readonly}
        {...rest}
      />
    </Container>
  );
};
